import React from 'react';
import { Globe, Workflow, CalendarDays, Mail, LayoutTemplate, MessageSquare, Filter, GitBranch, Star, CalendarCheck, Bot, Sparkles, GraduationCap, BarChart3, CreditCard } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

const tabs = [
  { icon: Globe, title: 'Command Center', description: 'Track what works' },
  { icon: Workflow, title: 'AI Workflows', description: 'Automate & Boost ROI' },
  { icon: CalendarDays, title: 'Social Planner', description: 'One Click to Everywhere' },
  { icon: Mail, title: 'Email Campaigns', description: 'Plan, Schedule, Publish.' },
  { icon: LayoutTemplate, title: 'Pre Built Templates', description: 'Ready to Convert' },
  { icon: MessageSquare, title: 'Unified Inbox', description: 'Centralize Communication' },
  { icon: Filter, title: 'AI Funnel Builder', description: 'Built for conversion, not just clicks' },
  { icon: GitBranch, title: 'Pipeline Manager', description: 'Visualize your revenue in real-time' },
  { icon: Star, title: 'Reputation Management', description: 'Collect 5-star reviews on autopilot' },
  { icon: CalendarCheck, title: 'Calendar Bookings', description: 'Turn clicks into confirmed meetings' },
  { icon: Bot, title: 'AI Agents', description: 'No PTO. No sickness. Always on.' },
  { icon: Sparkles, title: 'AI Content', description: 'Pro copy in seconds, not hours' },
  { icon: GraduationCap, title: 'Course Builder', description: 'Turn your expertise into an asset' },
  { icon: BarChart3, title: 'Ads Manager', description: 'Professional ad management, built right in' },
  { icon: CreditCard, title: 'Payments & Contracts', description: 'Seamless e-sign and instant deposits' },
];

interface IconTabsProps {
  activeTab: number;
  videoProgress: number;
  onTabClick: (index: number) => void;
}

const IconTabs: React.FC<IconTabsProps> = ({ activeTab, videoProgress, onTabClick }) => {
  const { theme } = useTheme();
  const activeTabData = tabs[activeTab];

  return (
    <div className="flex items-center gap-3 mb-3">
      {/* Icons grouped on the left - hidden on mobile */}
      <div className="hidden sm:flex items-center gap-1">
        {tabs.map((tab, index) => (
          <button
            key={index}
            onClick={() => onTabClick(index)}
            className="relative p-2 rounded-lg transition-colors"
            style={{
              backgroundColor: activeTab === index 
                ? (theme === 'dark' ? '#333333' : '#F5F5F5')
                : 'transparent'
            }}
          >
            <tab.icon 
              className="w-4 h-4" 
              style={{ 
                color: activeTab === index 
                  ? (theme === 'dark' ? '#FFFFFF' : '#111111')
                  : (theme === 'dark' ? '#666666' : '#999999')
              }} 
            />
            {activeTab === index && (
              <div 
                className="absolute bottom-0 left-0 h-0.5 transition-all"
                style={{ 
                  width: `${videoProgress}%`,
                  backgroundColor: theme === 'dark' ? '#FFFFFF' : '#111111'
                }}
              />
            )}
          </button>
        ))}
      </div>
      
      {/* Mobile: Show active tab icon */}
      <div className="flex sm:hidden items-center gap-2">
        <div 
          className="relative p-2 rounded-lg"
          style={{ backgroundColor: theme === 'dark' ? '#333333' : '#F5F5F5' }}
        >
          <activeTabData.icon 
            className="w-4 h-4" 
            style={{ color: theme === 'dark' ? '#FFFFFF' : '#111111' }} 
          />
          <div 
            className="absolute bottom-0 left-0 h-0.5 transition-all"
            style={{ 
              width: `${videoProgress}%`,
              backgroundColor: theme === 'dark' ? '#FFFFFF' : '#111111'
            }}
          />
        </div>
      </div>
      
      {/* Title and description on the right */}
      <div className="flex items-center gap-1.5" style={{ fontSize: '12px' }}>
        <span 
          className="font-semibold"
          style={{ color: theme === 'dark' ? '#FFFFFF' : '#111111' }}
        >
          {activeTabData.title}
        </span>
        <span className="hidden sm:inline" style={{ color: theme === 'dark' ? '#999999' : '#666666' }}>
          {activeTabData.description}
        </span>
      </div>
    </div>
  );
};

export { tabs };
export default IconTabs;
